<template>
  <div class="search-filter-container">
    <div v-if="enableTabs && tabs.length > 0" class="tabs-wrapper">
      <v-tabs 
        v-model="activeTab"
        background-color="transparent"
        class="custom-tabs"
        @change="handleTabChange"
      >
        <v-tab 
          v-for="tab in tabs"
          :key="tab.value"
          class="tab-item"
        >
          {{ tab.label }}
        </v-tab>
      </v-tabs>
    </div>

    <v-card
    outlined
    elevation="0"
      class="d-flex pa-4 ma-2 flex-column rounded-lg"
      :style="{ height: 'fit-content', flex: '1', 'background-color': cardColor, color: textColor }"
    >
      <div class="d-flex pa-2 flex-row align-center">
        <v-icon large class="mr-2" style="color: inherit">mdi-magnify</v-icon>
        <h3 style="flex-grow: 1; margin: 0">
          <slot name="header">{{ title }}</slot>
        </h3>
        <v-btn icon @click="display = !display" :class="{ 'pulse-animation': !display }">
          <v-icon>{{ display ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="display">
          <v-row style="width: 100%;" class="d-flex justify-start align-center max-width-col mt-2 search-filters-row" dense>
            <v-col
              v-for="(filter, index) in filters"
              :key="index"
              cols="12" md="3"
            >
              <component
                v-if="shouldShowFilter(filter)"
                :is="getFilterComponent(filter.type)"
                v-model="localSearch[filter.key]"
                v-bind="filter.props"
                :error-messages="errors[filter.key] || ''"
                @clear-error="clearFieldError(filter.key)"
                @search-input="val => onSearchInput(val, filter.key)"
                @update:search="val => $emit('filter-search-input', { key: filter.key, value: val })"
                @input="val => $emit('filter-value-change', { key: filter.key, value: val })"
              >
                <!-- Item slot -->
                <template 
                  v-if="filter.props && filter.props.scopedSlots && filter.props.scopedSlots.item" 
                  v-slot:item="scope"
                >
                  {{ filter.props.scopedSlots.item(scope) }}
                </template>
                
                <!-- Selection slot -->
                <template 
                  v-if="filter.props && filter.props.scopedSlots && filter.props.scopedSlots.selection" 
                  v-slot:selection="scope"
                >
                  {{ filter.props.scopedSlots.selection(scope) }}
                </template>
              </component>
            </v-col>

            <v-col cols="12" class="d-flex pt-8 pb-4 px-4">
              <v-btn elevation="0" outlined  :style="{ borderColor: '#378595', color: '#378595', backgroundColor: 'white',}" @click.stop="resetFilter" class="rounded-pill px-5 mr-2">Reset</v-btn>
              <v-btn elevation="0" color="primary rounded-pill px-5" @click.stop="handleSearchSubmit">Search</v-btn>
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script>
import { TextFieldFilter, SelectFilter, AutocompleteFilter, ComboboxFilter, DateRangeFilter } from './components';

export default {
  name: 'TableSearchFilter',
  components: {
    TextFieldFilter,
    SelectFilter,
    AutocompleteFilter,
    ComboboxFilter,
    DateRangeFilter
  },
  props: {
    search: {
      type: Object,
      required: true,
    },
    filters: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: 'Search',
    },
    textColor: {
      type: String,
      default: 'black',
    },
    cardColor: {
      type: String,
      default: 'white',
    },
    noResetCall: { 
      type: Boolean,
      default: false
    },
    enableTabs: {
      type: Boolean,
      default: false
    },
    tabs: {
      type: Array,
      default: () => []
    },
    defaultTabValue: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      localSearch: { ...this.search },
      display: true,
      errors: {},
      activeTab: null
    };
  },
  created() {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    let hasDefaultToday = false;
    
    this.filters.forEach(filter => {
      if (filter.type === 'daterange' && filter.props?.defaultToday) {
        const currentValue = this.localSearch[filter.key];
        if (!currentValue || currentValue.length === 0) {
          if (filter.props?.withTime) {
            this.localSearch[filter.key] = [`${today} 00:00:00`, `${today} 23:59:59`];
          } else {
            this.localSearch[filter.key] = [today, today];
          }
          hasDefaultToday = true;
        }
      }
    });
    
    // Emit initial search with default values to parent
    if (hasDefaultToday) {
      this.$emit('search', { ...this.localSearch });
    }
  },
  watch: {
    search: {
      handler(newSearch) {
        const filteredSearch = {};
        this.filters.forEach(filter => {
          filteredSearch[filter.key] = newSearch[filter.key];
          if (filteredSearch[filter.key] === undefined) {
            filteredSearch[filter.key] = Array.isArray(this.localSearch[filter.key]) ? [] : '';
          }
        });
        const hasChanges = Object.keys(filteredSearch).some(key => 
          JSON.stringify(this.localSearch[key]) !== JSON.stringify(filteredSearch[key])
        );
        
        if (hasChanges) {
          this.localSearch = filteredSearch;
        }
      },
      deep: true,
      immediate: true
    },
    defaultTabValue: {
      handler(newVal) {
        if (newVal && this.enableTabs) {
          const tabIndex = this.tabs.findIndex(tab => tab.value === newVal);
          if (tabIndex !== -1) {
            this.activeTab = tabIndex;
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    shouldShowFilter(filter) {
      return filter.show !== false;
    },
    getFilterComponent(type) {
      switch (type) {
        case 'textfield':
          return 'TextFieldFilter';
        case 'select':
          return 'SelectFilter';
        case 'autocomplete':
          return 'AutocompleteFilter';
        case 'combobox':
          return 'ComboboxFilter';
        case "daterange":
          return "DateRangeFilter";
        default:
          throw new Error(`Unknown filter type: ${type}`);
      }
    },
    handleSearch() {
      this.$emit('search', { ...this.localSearch });
    },
    handleTabChange(tabIndex) {
      const activeTabValue = this.tabs[tabIndex]?.value;
      if (activeTabValue) {
        this.$emit('tab-change', {
          tabValue: activeTabValue,
          tabIndex,
          tabLabel: this.tabs[tabIndex]?.label
        });
      }
    },
    handleSearchSubmit() {
      this.errors = {}; 

      this.filters.forEach((filter) => {
        const value = this.localSearch[filter.key];

        if (filter.required) {
          if (Array.isArray(value) && value.length === 0) {
            this.errors[filter.key] = `${filter.props.label} is required`;
          } else if (!value) {
            this.errors[filter.key] = `${filter.props.label} is required`;
          }
        }
      });
      if (Object.keys(this.errors).length > 0) {
        this.$nextTick(() => {
          const firstErrorField = document.querySelector(".v-messages__message");
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
        return;
      }

      const payload = { ...this.localSearch };
      if (this.enableTabs && this.activeTab !== null) {
        payload.activeTab = this.tabs[this.activeTab]?.value;
      }

      this.$emit("showType", payload);

      const element = document.getElementById("floating-arrow");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    clearFieldError(fieldKey) {
      if (this.errors[fieldKey]) {
        this.$delete(this.errors, fieldKey);
      }
    },
    resetFilter() {
      const emptySearch = {};
      this.filters.forEach(filter => {
        emptySearch[filter.key] = Array.isArray(this.localSearch[filter.key]) ? [] : '';
      });
      
      this.localSearch = emptySearch;
      Object.keys(emptySearch).forEach(key => {
        this.$emit('filter-value-change', { key, value: emptySearch[key] });
      });
      
      this.errors = {};
    },
    getActiveTabValue() {
      return this.activeTab !== null ? this.tabs[this.activeTab]?.value : null;
    }
  },
};
</script>

<style scoped>
@keyframes pulse-animation {
  0% {
    box-shadow: 0 0 0 0px #cedad2;
  }
  100% {
    box-shadow: 0 0 0 15px rgba(0, 0, 0, 0);
  }
}

.pulse-animation {
  animation: pulse-animation 2s infinite;
  animation-delay: 1s
}

.search-filters-row > .v-col,
.search-filters-row .v-col {
  padding-left: 6px !important;
  padding-right: 6px !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.search-filter-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px;
  gap: 0;
}

.tabs-wrapper {
  display: flex;
  gap: 8px;
  padding: 0 8px;
  margin-bottom: -4px;
}

::v-deep .custom-tabs {
  background-color: transparent !important;
  width: fit-content !important;
}

::v-deep .custom-tabs .v-tabs__bar {
  background-color: transparent !important;
  border: none !important;
  height: auto !important;
}

::v-deep .custom-tabs .tab-item {
  min-width: 100px !important;
  background-color: #ffff;
  color: #378595 !important;
  border: 1px solid #378595 !important;
  font-weight: 600;
  border-radius: 0;
  margin-right: 0 !important;
  padding: 12px 20px !important;
  text-transform: uppercase;
  font-size: 14px;
}

::v-deep .custom-tabs .tab-item.v-tab--active {
  background-color: #378595 !important;
  color: white !important;
}

::v-deep .custom-tabs .tab-item:last-child {
  border-radius: 0px 20px 0 0 !important;
}

::v-deep .custom-tabs .v-tab {
  text-transform: uppercase;
}

::v-deep .custom-tabs .v-tabs__slider {
  display: none !important;
}

</style>

